import React, {useContext, useState} from "react";
import {useSelector} from "react-redux";
import {getMessageTime} from "utilities/formatUtilities";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import ICONS from "connectui/icons/iconMap";
// Components
import Skeleton from "connectui/component/display/Skeleton";
import FlexContainer from "connectui/component/layout/FlexContainer";
import ProfilePicture from "connectui/component/display/ProfilePicture";
import Text from "connectui/component/display/Text";
import Container from "connectui/component/layout/Container";
import MessageContentBase from "./content/MessageContentBase";
import MessageContentAttachment from "./content/MessageContentAttachment";
import MessageNotification from "./content/MessageNotification";
import MessageContentEmail from "./content/MessageContentEmail";
import MessageNote from "./content/MessageNote";
import MessageContentTypingIndicator from "./content/MessageContentTyping";
import SvgIcon from "connectui/component/display/SvgIcon";
import ButtonIconText from "connectui/component/input/button/ButtonIconText";
import MessageStatusTooltip from "./content/MessageStatusTooltip";
import {MessagesContext} from "../../panel/conversations/ConversationsPanel";
import {useTranslation} from "react-i18next";

const channelIcons = {
  airbnb: ICONS["airbnb-circle"],
  SMS: ICONS["phone-card"],
  email: ICONS.email,
  whatsapp: ICONS["whatsapp-circle"],
  vrbo: ICONS["vrbo-circle"],
  booking_com: ICONS["bookingcom-circle"],
  homeaway: ICONS["homeaway-circle"],
};

const useStyles = (theme) =>
  buildStyles({
    tag: {
      padding: theme.spacing(0.5, 2),
      borderRadius: theme.spacing(4),
      backgroundColor: theme.colors.gray100,
    },
    noteIcon: {
      width: "14px",
      height: "14px",
    },
    channelIcon: {
      width: "22px",
      height: "22px",
      border: `1px solid ${theme.colors.white}`,
      borderRadius: "50%",
      padding: "1px",
    },
    translateBtn: {
      border: "none",
      outline: "none",
      boxShadow: "none",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      padding: 0,
      gap: theme.spacing(1),
      backgroundColor: "transparent",
      "&:hover": {backgroundColor: "transparent"},
    },
    errorMessage: {
      maxWidth: "300px",
      overflow: "hidden",
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      "&:hover": {
        color: theme.colors.error700,
      },
    },
    errorText: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    errorTextExpanded: {
      whiteSpace: "normal",
      wordBreak: "break-word",
    },
    expandIcon: {
      width: "14px",
      height: "14px",
      flexShrink: 0,
      transition: "transform 0.2s ease",
    },
    expandIconRotated: {
      transform: "rotate(180deg)",
    },
  });

const getTranslation = (translations = [], userLanguage = "en") => {
  return translations.find((t) => t.language === userLanguage)?.value ?? null;
};

const MessageBubble = ({
  skeleton = {enabled: false, width: "80%", height: "80px"},
  sender = "user",
  guest,
  msg = {},
  ...props
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const [isErrorExpanded, setIsErrorExpanded] = useState(false);
  const {t} = useTranslation();

  const {
    enso_user_id,
    attachments,
    notification_type,
    notification_id,
    sent,
    channel,
    message,
    sending_error,
    message_translations,
  } = msg;

  const {setReplyEmailProps} = useContext(MessagesContext);
  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );
  const [translation, setTranslation] = useState(
    getTranslation(message_translations, user_profile?.language),
  );

  const sentBy =
    user_profile?.enso_users?.find((eu) => eu.enso_user_id === enso_user_id)
      ?.name ?? "";
  const hasTranslations = !!getTranslation(
    message_translations,
    user_profile?.language,
  );
  const showMessageActions = hasTranslations; // Add more actions to the message bubble
  const hasAttachments = !!attachments?.length;

  const isGuestSender = sender === "other_user";
  const isHostSender = [
    "user",
    "automessage",
    "chatbot",
    "auto_chatbot",
  ].includes(sender);
  const isAutomessage = sender === "automessage";
  const isEnsoAI = sender === "chatbot";
  const isAutopilot = sender === "auto_chatbot";
  const isNote = channel === "note";
  const isNotification = !!notification_type || !!notification_id;
  const isEmail =
    channel === "email" ||
    (message &&
      typeof message === "object" &&
      ("html" in message || "html_url" in message)) ||
    (typeof message === "string" &&
      message.toLowerCase().startsWith("<!doctype html"));
  const isTypingIndicator = msg?.isTyping;

  const replyEmail = (subject) => {
    setReplyEmailProps({
      enabled: true,
      subject,
    });
  };

  function renderContent() {
    if (isTypingIndicator) {
      return <MessageContentTypingIndicator />;
    } else if (isNotification) {
      return (
        <MessageNotification
          message={msg}
          textColor={!!msg.errors?.length ? "error600" : "textSecondary"}
          {...props}
        />
      );
    } else if (hasAttachments) {
      return (
        <MessageContentAttachment
          isGuestSender={isGuestSender}
          hasAttachments={hasAttachments}
          isHostSender={isHostSender}
          translation={translation}
          replyEmail={replyEmail}
          message={msg}
          isEmail={isEmail && (!hasTranslations || !translation)}
          {...props}
        />
      );
    } else if (isEmail && (!hasTranslations || !translation)) {
      return (
        <MessageContentEmail
          isGuestSender={isGuestSender}
          isHostSender={isHostSender}
          translation={translation}
          message={msg}
          replyEmail={replyEmail}
          {...props}
        />
      );
    } else if (isNote) {
      return <MessageNote message={msg} {...props} />;
    } else {
      return (
        <MessageContentBase
          isGuestSender={isGuestSender}
          isHostSender={isHostSender}
          translation={translation}
          message={msg}
          {...props}
        />
      );
    }
  }

  if (isNotification) return renderContent();
  return (
    <FlexContainer
      gap={theme.spacing(3)}
      justifyContent={isGuestSender ? "flex-start" : "flex-end"}
      sx={{width: "100%", height: skeleton.enabled ? skeleton.height : "auto"}}
    >
      {/* Guest sender avatar */}
      {isGuestSender && (
        <ProfilePicture
          sx={{minWidth: "40px"}}
          name={guest?.name ?? ""}
          skeleton={{enabled: skeleton.enabled}}
          badges={[
            <SvgIcon
              component={channelIcons[channel]}
              className={classes.channelIcon}
            />,
          ]}
        />
      )}

      <FlexContainer
        flexDirection="column"
        alignItems={isGuestSender ? "flex-start" : "flex-end"}
        gap={theme.spacing(1.5)}
        sx={{
          height: "100%",
          width: skeleton.enabled ? skeleton.width : isEmail ? "100%" : "auto",
          maxWidth: "85%",
        }}
      >
        <FlexContainer
          alignItems="center"
          justifyContent="space-between"
          gap={theme.spacing(2)}
          sx={{width: "100%"}}
        >
          {/* Message information */}
          {skeleton.enabled ? (
            <Skeleton
              width={"72px"}
              height={"20px"}
              style={{borderRadius: `${theme.radius.lg}px`}}
            />
          ) : (
            <FlexContainer alignItems="center" gap={theme.spacing(2)}>
              <Text as="div" variant="textSM" weight="medium" color="gray700">
                {isNote ? (
                  <FlexContainer alignItems="center" gap={theme.spacing(1)}>
                    <SvgIcon
                      component={ICONS.note}
                      className={classes.noteIcon}
                      sx={{color: theme.colors.gray500}}
                    />
                    {user_profile.enso_user_id === enso_user_id
                      ? "Your Note"
                      : !!sentBy
                        ? `${sentBy}'s Note`
                        : "Note"}
                  </FlexContainer>
                ) : !!enso_user_id ? (
                  sentBy
                ) : isGuestSender ? (
                  (guest?.name ?? "")
                ) : !enso_user_id ? (
                  ""
                ) : (
                  "You"
                )}
              </Text>
              {isAutomessage && (
                <Text
                  className={classes.tag}
                  variant="textXS"
                  weight="medium"
                  color="gray700"
                >
                  {"Auto-message"}
                </Text>
              )}
              {isAutopilot && (
                <Text
                  className={classes.tag}
                  variant="textXS"
                  weight="medium"
                  color="gray700"
                >
                  {"EnsoAI Autopilot"}
                </Text>
              )}
              {!isEnsoAI &&
                !isAutomessage &&
                !isAutopilot &&
                !enso_user_id &&
                !isGuestSender && (
                  <Text
                    className={classes.tag}
                    variant="textXS"
                    weight="medium"
                    color="gray700"
                  >
                    {"Imported Message"}
                  </Text>
                )}
              {isEnsoAI && (
                <Text
                  className={classes.tag}
                  variant="textXS"
                  weight="medium"
                  color="gray700"
                >
                  {"EnsoAI"}
                </Text>
              )}
            </FlexContainer>
          )}
          {skeleton.enabled ? (
            <Skeleton
              width={"72px"}
              height={"20px"}
              style={{borderRadius: `${theme.radius.lg}px`}}
            />
          ) : (
            <Text variant="textXS" color="gray600">
              {getMessageTime(sent)}
            </Text>
          )}
        </FlexContainer>

        {/* Message content */}
        {skeleton.enabled ? (
          <Skeleton
            width={"100%"}
            height={"100%"}
            style={{borderRadius: `${theme.radius.lg}px`}}
          />
        ) : (
          <FlexContainer
            alignItems="center"
            justifyContent={isGuestSender ? "flex-start" : "flex-end"}
            gap={theme.spacing(1)}
            sx={{width: "100%"}}
          >
            {renderContent()}
            {msg?.tags?.autoresponder_failure_reasons?.length > 0 && (
              <MessageStatusTooltip
                message={msg}
                className={classes.channelIcon}
              />
            )}
          </FlexContainer>
        )}
        {showMessageActions && (
          <FlexContainer
            alignItems="center"
            gap={theme.spacing(2)}
            sx={{alignSelf: "start", marginTop: theme.spacing(1.5)}}
          >
            {!!message_translations?.length && (
              <ButtonIconText
                className={classes.translateBtn}
                label={!!translation ? "Hide Translation" : ""}
                weight="medium"
                typographyVariant="textSM"
                color={!!translation ? "primary" : "textSecondary"}
                onClick={() =>
                  setTranslation(
                    !!translation
                      ? null
                      : getTranslation(
                          message_translations,
                          user_profile?.language,
                        ),
                  )
                }
                icon={
                  <SvgIcon
                    component={ICONS.translate}
                    sx={{
                      width: "18px",
                      height: "18px",
                      color: !!translation
                        ? theme.colors.primary
                        : theme.colors.textSecondary,
                    }}
                  />
                }
              />
            )}
          </FlexContainer>
        )}
        {(sending_error || msg.delivery_state === "failed") && (
          <FlexContainer alignItems="center" gap={theme.spacing(1)}>
            <SvgIcon
              component={ICONS["alert-circle"]}
              sx={{
                width: "17px",
                height: "17px",
                color: theme.colors.error600,
                padding: theme.spacing(0.5),
              }}
            />
            <Container
              onClick={() => setIsErrorExpanded(!isErrorExpanded)}
              className={classes.errorMessage}
            >
              <Text
                variant="textXS"
                color="error600"
                className={
                  isErrorExpanded
                    ? classes.errorTextExpanded
                    : classes.errorText
                }
              >
                {t("couldn't-send")}
                {msg.error_message ? `: ${msg.error_message}` : ""}
              </Text>
              <SvgIcon
                component={ICONS["chevron-down"]}
                className={`${classes.expandIcon} ${isErrorExpanded ? classes.expandIconRotated : ""}`}
                sx={{color: theme.colors.error600}}
              />
            </Container>
          </FlexContainer>
        )}
      </FlexContainer>

      {/* Host sender avatar */}
      {!isGuestSender && (
        <ProfilePicture
          sx={{minWidth: "40px"}}
          name={sentBy}
          skeleton={{enabled: skeleton.enabled}}
          svgIcon={
            isTypingIndicator || isAutopilot
              ? ICONS["send-autopilot"]
              : isAutomessage
                ? ICONS.zap
                : !enso_user_id && !isAutomessage
                  ? ICONS["file-import"]
                  : undefined
          }
          badges={
            channel === "note" || isTypingIndicator
              ? []
              : [
                  <SvgIcon
                    component={channelIcons[channel]}
                    className={classes.channelIcon}
                  />,
                ]
          }
        />
      )}
    </FlexContainer>
  );
};

export default MessageBubble;
